import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RangeTabs } from "@/components/PriceHistoryChart";
import { fmtInTz } from "@/lib/utils";

const COLORS = ["#F59E0B", "#38BDF8", "#34D07F", "#F472B6", "#A78BFA", "#FB7185"];

export default function Compare() {
  const nav = useNavigate();
  const { user } = useAuth();
  const tz = user?.timezone || "UTC";
  const [picked, setPicked] = useState(["gold", "silver"]);
  const [hidden, setHidden] = useState([]);
  const [range, setRange] = useState("7d");
  const [mode, setMode] = useState("pct");
  const { data: prices } = useQuery({ queryKey: ["prices"], queryFn: async () => (await api.get("/prices")).data.items });
  const { data, isLoading } = useQuery({
    queryKey: ["compare", picked.join(","), range],
    queryFn: async () => (await api.get("/price-compare", { params: { symbols: picked.join(","), range } })).data.series,
    enabled: picked.length >= 2,
  });
  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : p.length >= 6 ? p : [...p, s]));
  const series = (data || []).map((s, i) => {
    const base = s.points[0]?.price || 1;
    return { ...s, color: COLORS[picked.indexOf(s.symbol) % 6] || COLORS[i],
             data: s.points.map((p) => ({ t: p.t, v: mode === "pct" ? +(((p.price - base) / base) * 100).toFixed(2) : p.price })) };
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="compare-page">
      <div className="sticky top-0 z-30 glass flex items-center gap-3 px-4 pb-3 pt-4">
        <button onClick={() => nav(-1)} data-testid="compare-back" className="pressable glass flex h-10 w-10 items-center justify-center rounded-full"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-2xl font-extrabold leading-none">Compare Prices</h1>
          <p className="mt-1 text-[11px] text-dim">Pick 2–6 commodities to overlay their trends</p>
        </div>
      </div>
      <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto px-4">
        {(prices || []).map((p) => (
          <button key={p.symbol} onClick={() => toggle(p.symbol)} data-active={picked.includes(p.symbol)} data-testid={`compare-pick-${p.symbol}`}
                  className="chip pressable shrink-0 px-3.5 py-2 text-sm font-semibold">{p.name}</button>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 px-4">
        <div className="flex-1"><RangeTabs value={range} onChange={setRange} prefix="compare-range" /></div>
        <div className="glass flex rounded-2xl p-1">
          {[["pct", "% Change"], ["price", "Price"]].map(([id, l]) => (
            <button key={id} onClick={() => setMode(id)} data-testid={`compare-mode-${id}`} className="pressable rounded-xl px-3 py-1.5 text-xs font-bold"
                    style={{ background: mode === id ? "var(--surface-2)" : "transparent", color: mode === id ? "var(--accent)" : "var(--text-dim)" }}>{l}</button>
          ))}
        </div>
      </div>
      <div className="px-4">
        <div className="goe-card mt-3 p-3" style={{ height: 300 }} data-testid="compare-chart">
          {picked.length < 2 ? <Empty text="Select at least 2 commodities to compare." /> : isLoading ? <div className="skeleton h-full w-full" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="t" type="number" domain={["dataMin", "dataMax"]} allowDuplicatedCategory={false} minTickGap={40}
                       tickFormatter={(t) => fmtInTz(t, tz, range === "24h" ? { hour: "2-digit", minute: "2-digit" } : { day: "numeric", month: "short" }).replace(/ \w+$/, "")}
                       tick={{ fontSize: 10, fill: "var(--text-dim)" }} axisLine={false} tickLine={false} />
                {mode === "pct" ? <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "var(--text-dim)" }} width={44} axisLine={false} tickLine={false} />
                  : series.map((s) => <YAxis key={s.symbol} yAxisId={s.symbol} domain={["dataMin", "dataMax"]} hide />)}
                <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--text)" }}
                         labelFormatter={(t) => fmtInTz(t, tz)} formatter={(v, n) => [mode === "pct" ? `${v}%` : `$${v.toLocaleString()}`, n]} />
                {series.filter((s) => !hidden.includes(s.symbol)).map((s) => (
                  <Line key={s.symbol} data={s.data} dataKey="v" name={s.name} stroke={s.color} strokeWidth={2.2} dot={false}
                        isAnimationActive={false} {...(mode === "price" ? { yAxisId: s.symbol } : {})} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {series.map((s) => {
            const pts = s.points; const chg = pts.length > 1 ? ((pts[pts.length - 1].price - pts[0].price) / pts[0].price) * 100 : 0;
            const off = hidden.includes(s.symbol);
            return (
              <button key={s.symbol} onClick={() => setHidden((h) => (off ? h.filter((x) => x !== s.symbol) : [...h, s.symbol]))}
                      data-testid={`compare-legend-${s.symbol}`} className="goe-card pressable flex w-full items-center gap-3 p-3 text-left" style={{ opacity: off ? 0.45 : 1 }}>
                <span className="h-3 w-3 rounded-full" style={{ background: s.color }} />
                <span className="flex-1 font-bold">{s.name}{!s.live && <span className="ml-2 text-[10px] font-semibold text-dim">INDICATIVE</span>}</span>
                <span className="text-sm font-bold" style={{ color: chg >= 0 ? "var(--green)" : "var(--red)" }}>{chg >= 0 ? "+" : ""}{chg.toFixed(2)}%</span>
                {off ? <EyeOff size={16} className="text-dim" /> : <Eye size={16} className="text-dim" />}
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

const Empty = ({ text }) => <div className="flex h-full items-center justify-center text-sm text-dim" data-testid="compare-empty">{text}</div>;
